import { __decorate, __metadata, __param } from "tslib";
import { inject, injectable } from 'inversify';
import { CompanyDataAnalysisOptions, defaultChartOrTableHeight } from './CompanyDataAnalysisService';
import { Symbols } from '@/symbols';
import { CompanyDataAnalysisViewType } from './CompanyDataAnalysis.type';
import { CompanyDataAnalysisDataSet, CompanyDataAnalysisEventKey, CompanyDataAnalysisKeyType } from '@/apis/service/company/CompanyDataAnalysisApi.type';
import { GoodTablePaginationOptions } from '@/components/GoodTable/GoodTable.types';
let CompanyConnectDataAnalysisService = class CompanyConnectDataAnalysisService {
    constructor(companyDataAnalysisService, companyDataAnalysisApi, companyConnectApi, i18n) {
        this.companyDataAnalysisService = companyDataAnalysisService;
        this.companyDataAnalysisApi = companyDataAnalysisApi;
        this.companyConnectApi = companyConnectApi;
        this.i18n = i18n;
        // Usage Details
        this.usageDetailOption = new CompanyDataAnalysisOptions();
        // Successful Calls
        this.connectUsageOption = new CompanyDataAnalysisOptions();
        // Attempted Calls
        this.connectAttemptedOption = new CompanyDataAnalysisOptions();
        // Successful Calls Ranking
        this.successfulCallsRankingOption = new CompanyDataAnalysisOptions();
        // Attempted Calls Ranking
        this.attemptedCallsRankingOption = new CompanyDataAnalysisOptions();
        this.connectUsagePvConfigInit();
        this.connectAttemptedConfigInit();
        this.usageDetailConfigInit();
        this.successfulCallsRankingConfigInit();
        this.attemptedCallsRankingConfigInit();
    }
    usageDetailConfigInit() {
        this.usageDetailOption.title = this.i18n.t('backoffice.company.dataAnalysis.connect.usageDetailTitle');
        this.usageDetailOption.tips = [
            {
                desc: this.i18n.t('backoffice.company.dataAnalysis.connect.usageDetailTitleTip'),
            }
        ];
        this.usageDetailOption.reportName = CompanyDataAnalysisKeyType.CONNECT_USAGE_DETAIL;
        this.usageDetailOption.viewType = CompanyDataAnalysisViewType.Table;
        this.usageDetailOption.canCollapse = true;
        this.usageDetailOption.canChangeViewType = false;
        this.usageDetailOption.statistics = [];
        this.usageDetailOption.tableFields = [
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.apiName'),
                field: 'api_name',
                sortable: false,
                width: '150px',
                checked: true,
            },
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.requestPath'),
                field: 'request_path',
                sortable: false,
                width: '200px',
                checked: true,
            },
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.consumptionProportion'),
                field: 'consumption_proportion',
                sortable: true,
                width: '160px',
                checked: true,
            },
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.successfulCallsProportion'),
                field: 'successful_calls_proportion',
                sortable: false,
                width: '180px',
                checked: true,
                tooltip: this.i18n.t('backoffice.company.dataAnalysis.connect.successfulCallsProportionTip'),
            },
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.attemptedCallsTotal'),
                field: 'call_count',
                sortable: true,
                width: '180px',
                checked: true,
            },
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.successfulCallsTotal'),
                field: 'fee_times',
                sortable: true,
                width: '180px',
                checked: true,
            },
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.attemptedCallsFixed'),
                field: 'combo_call_count',
                sortable: true,
                width: '220px',
                checked: true,
            },
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.successfulCallsFixed'),
                field: 'combo_fee_times',
                sortable: true,
                width: '220px',
                checked: true,
            },
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.attemptedCallsPrepaid'),
                field: 'attempted_calls_prepaid',
                sortable: true,
                width: '220px',
                checked: true,
            },
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.successfulCallsPrepaid'),
                field: 'successful_calls_prepaid',
                sortable: true,
                width: '220px',
                checked: true,
            }
        ];
        this.usageDetailOption.tableOptions.columns = this.usageDetailOption.tableFields;
        const paginationOptions = new GoodTablePaginationOptions();
        paginationOptions.enabled = true;
        paginationOptions.perPage = 10;
        this.usageDetailOption.tableOptions.paginationOptions = paginationOptions;
    }
    successfulCallsRankingConfigInit() {
        this.successfulCallsRankingOption.title = this.i18n.t('backoffice.company.dataAnalysis.connect.successfulCallsRanking');
        this.successfulCallsRankingOption.reportName = 'connect_successful_calls_ranking';
        this.successfulCallsRankingOption.viewType = CompanyDataAnalysisViewType.Chart;
        this.successfulCallsRankingOption.canCollapse = false;
        this.successfulCallsRankingOption.canChangeViewType = true;
        this.successfulCallsRankingOption.statistics = [];
        this.successfulCallsRankingOption.chartId = 'successfulCallsRankingChart';
        this.successfulCallsRankingOption.chartHeight = defaultChartOrTableHeight.defaultChartHeight;
        this.successfulCallsRankingOption.tableOptions.maxHeight = defaultChartOrTableHeight.defaultTableHeight;
        // 启用分页
        const paginationOptions = new GoodTablePaginationOptions();
        paginationOptions.enabled = true;
        paginationOptions.perPage = 10;
        this.successfulCallsRankingOption.tableOptions.paginationOptions = paginationOptions;
        // 表格字段配置
        this.successfulCallsRankingOption.tableFields = [
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.apiName'),
                field: 'api_name',
                width: '200px',
                checked: true,
            },
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.fixedQuotaPlan'),
                field: 'combo_fee_times',
                width: '180px',
                checked: true,
            },
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.prepaidModel'),
                field: 'fee_times',
                width: '180px',
                checked: true,
            },
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.percentage'),
                field: 'percentage',
                width: '150px',
                checked: true,
            }
        ];
        this.successfulCallsRankingOption.tableOptions.columns = this.successfulCallsRankingOption.tableFields;
    }
    attemptedCallsRankingConfigInit() {
        this.attemptedCallsRankingOption.title = this.i18n.t('backoffice.company.dataAnalysis.connect.attemptedCallsRanking');
        this.attemptedCallsRankingOption.reportName = 'connect_attempted_calls_ranking';
        this.attemptedCallsRankingOption.viewType = CompanyDataAnalysisViewType.Chart;
        this.attemptedCallsRankingOption.canCollapse = false;
        this.attemptedCallsRankingOption.canChangeViewType = true;
        this.attemptedCallsRankingOption.statistics = [];
        this.attemptedCallsRankingOption.chartId = 'attemptedCallsRankingChart';
        this.attemptedCallsRankingOption.chartHeight = defaultChartOrTableHeight.defaultChartHeight;
        this.attemptedCallsRankingOption.tableOptions.maxHeight = defaultChartOrTableHeight.defaultTableHeight;
        // 启用分页
        const paginationOptions = new GoodTablePaginationOptions();
        paginationOptions.enabled = true;
        paginationOptions.perPage = 10;
        this.attemptedCallsRankingOption.tableOptions.paginationOptions = paginationOptions;
        // 表格字段配置
        this.attemptedCallsRankingOption.tableFields = [
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.apiName'),
                field: 'api_name',
                width: '200px',
                checked: true,
            },
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.fixedQuotaPlan'),
                field: 'combo_call_count',
                width: '180px',
                checked: true,
            },
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.prepaidModel'),
                field: 'call_count',
                width: '180px',
                checked: true,
            },
            {
                label: this.i18n.t('backoffice.company.dataAnalysis.connect.percentage'),
                field: 'percentage',
                width: '150px',
                checked: true,
            }
        ];
        this.attemptedCallsRankingOption.tableOptions.columns = this.attemptedCallsRankingOption.tableFields;
    }
    connectUsagePvConfigInit() {
        this.connectUsageOption.title = this.i18n.t('backoffice.company.dataAnalysis.connect.successfulCallsTrend');
        this.connectUsageOption.tips = [
            {
                desc: this.i18n.t('backoffice.company.dataAnalysis.connect.successfulCallsTip'),
            }
        ];
        this.connectUsageOption.reportName = CompanyDataAnalysisKeyType.CONNECT_USAGE;
        this.connectUsageOption.viewType = CompanyDataAnalysisViewType.Chart;
        this.connectUsageOption.canCollapse = true;
        this.connectUsageOption.canChangeViewType = true; // 允许切换图表和列表视图
        this.connectUsageOption.tableOptions.rows = [];
        this.connectUsageOption.chartOption = {};
        this.connectUsageOption.chartId = 'connectUsageChart'; // 设置唯一的图表ID
        this.connectUsageOption.chartHeight = defaultChartOrTableHeight.defaultChartHeight;
        this.connectUsageOption.tableOptions.maxHeight = defaultChartOrTableHeight.defaultTableHeight; // 设置表格最大高度
        this.connectUsageOption.statistics = []; // 不显示统计信息块
        // 设置固定的表格列
        this.connectUsageOption.tableOptions.columns = [
            { label: this.i18n.t('backoffice.company.dataAnalysis.date'), field: CompanyDataAnalysisEventKey.DATE, width: '120px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.connect.fixed'), field: CompanyDataAnalysisEventKey.FIXED, width: '120px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.connect.prepaid'), field: CompanyDataAnalysisEventKey.PREPAID, width: '120px' }
        ];
    }
    connectAttemptedConfigInit() {
        this.connectAttemptedOption.title = this.i18n.t('backoffice.company.dataAnalysis.connect.attemptedCallsTrend');
        this.connectAttemptedOption.tips = [
            {
                desc: this.i18n.t('backoffice.company.dataAnalysis.connect.attemptedCallsTip'),
            }
        ];
        this.connectAttemptedOption.reportName = CompanyDataAnalysisKeyType.CONNECT_ATTEMPTED;
        this.connectAttemptedOption.viewType = CompanyDataAnalysisViewType.Chart;
        this.connectAttemptedOption.canCollapse = true;
        this.connectAttemptedOption.canChangeViewType = true; // 允许切换图表和列表视图
        this.connectAttemptedOption.tableOptions.rows = [];
        this.connectAttemptedOption.chartOption = {};
        this.connectAttemptedOption.chartId = 'connectAttemptedChart'; // 设置唯一的图表ID
        this.connectAttemptedOption.chartHeight = defaultChartOrTableHeight.defaultChartHeight;
        this.connectAttemptedOption.tableOptions.maxHeight = defaultChartOrTableHeight.defaultTableHeight; // 设置表格最大高度
        this.connectAttemptedOption.statistics = []; // 不显示统计信息块
        // 设置固定的表格列
        this.connectAttemptedOption.tableOptions.columns = [
            { label: this.i18n.t('backoffice.company.dataAnalysis.date'), field: CompanyDataAnalysisEventKey.DATE, width: '120px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.connect.fixed'), field: CompanyDataAnalysisEventKey.FIXED, width: '120px' },
            { label: this.i18n.t('backoffice.company.dataAnalysis.connect.prepaid'), field: CompanyDataAnalysisEventKey.PREPAID, width: '120px' }
        ];
    }
    async initData(params) {
        // 先获取 Trend 数据，确保 Updated 时间被设置
        await Promise.all([
            this.getConnectUsageData(params),
            this.getConnectAttemptedData(params)
        ]);
        // 再获取 Ranking 和其他数据
        this.getUsageDetailData(params);
        this.getSuccessfulCallsRankingData(params);
        this.getAttemptedCallsRankingData(params);
    }
    async getSuccessfulCallsRankingData(params) {
        this.successfulCallsRankingOption.loading = true;
        try {
            const iDisplayStart = params.iDisplayStart || 0;
            const iDisplayLength = params.iDisplayLength || this.successfulCallsRankingOption.tableOptions.paginationOptions.perPage;
            const response = await this.companyConnectApi.getRankingApiCall({
                company_id: params.company_id,
                begin_at: typeof params.begin_at === 'number' ? params.begin_at : Number(params.begin_at),
                end_at: typeof params.end_at === 'number' ? params.end_at : Number(params.end_at),
                granularity: params.granularity,
                report_name: 'openapi_successful_called_ranking',
            }, {
                iDisplayStart,
                iDisplayLength,
            });
            const data = response.aaData || [];
            this.successfulCallsRankingOption.tableOptions.totalRows = response.iTotalRecords;
            this.successfulCallsRankingOption.noData = data.length === 0;
            // 从对应的 Trend 中获取 Last Updated 时间
            this.successfulCallsRankingOption.Updated = this.connectUsageOption.Updated;
            if (this.successfulCallsRankingOption.noData)
                return;
            // 计算所有 API 的总调用次数（用于计算百分比）
            const totalCalls = data.reduce((sum, item) => sum + item.combo_fee_times + item.fee_times, 0);
            // 添加排名、计算总数和百分比
            const processedData = data.map((item, index) => {
                const itemTotal = item.combo_fee_times + item.fee_times;
                const percentage = totalCalls > 0 ? (itemTotal / totalCalls * 100).toFixed(2) : '0.00';
                return {
                    ...item,
                    ranking: iDisplayStart + index + 1,
                    total_fee_times: itemTotal,
                    percentage: percentage,
                };
            });
            // 生成表格数据
            this.successfulCallsRankingOption.tableOptions.rows = processedData;
            this.successfulCallsRankingOption.tableOptions.paginationOptions.setCurrentPage = (iDisplayStart / iDisplayLength) + 1;
            // 生成图表数据 - 水平条形图
            const chartOption = {
                grid: {
                    left: '5%',
                    right: '5%',
                    top: '5%',
                    bottom: '5%',
                    containLabel: true,
                },
                xAxis: {
                    type: 'value',
                },
                yAxis: {
                    type: 'category',
                    data: processedData.map((item, index) => `${index + 1} ${this.i18n.locale === 'en' ? item.api_en_name : item.api_cn_name}`),
                    inverse: true,
                    axisLabel: {
                        interval: 0,
                        formatter: (value) => {
                            const maxLength = 20;
                            if (value.length > maxLength) {
                                return value.substring(0, maxLength) + '...';
                            }
                            return value;
                        },
                    },
                },
                series: [
                    {
                        name: this.i18n.t('backoffice.company.dataAnalysis.connect.fixed'),
                        type: 'bar',
                        stack: 'total',
                        data: processedData.map(item => item.combo_fee_times),
                        itemStyle: {
                            color: '#5470C6',
                        },
                    },
                    {
                        name: this.i18n.t('backoffice.company.dataAnalysis.connect.prepaid'),
                        type: 'bar',
                        stack: 'total',
                        data: processedData.map(item => item.fee_times),
                        itemStyle: {
                            color: '#91CC75',
                        },
                    }
                ],
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow',
                    },
                },
                legend: {
                    data: [
                        this.i18n.t('backoffice.company.dataAnalysis.connect.fixed'),
                        this.i18n.t('backoffice.company.dataAnalysis.connect.prepaid')
                    ],
                    bottom: 0,
                },
            };
            this.successfulCallsRankingOption.chartOption = chartOption;
        }
        catch (error) {
            console.error('Failed to load successful calls ranking:', error);
            this.successfulCallsRankingOption.noData = true;
        }
        finally {
            this.successfulCallsRankingOption.loading = false;
        }
    }
    async getAttemptedCallsRankingData(params) {
        this.attemptedCallsRankingOption.loading = true;
        try {
            const iDisplayStart = params.iDisplayStart || 0;
            const iDisplayLength = params.iDisplayLength || this.attemptedCallsRankingOption.tableOptions.paginationOptions.perPage;
            const response = await this.companyConnectApi.getRankingApiCall({
                company_id: params.company_id,
                begin_at: typeof params.begin_at === 'number' ? params.begin_at : Number(params.begin_at),
                end_at: typeof params.end_at === 'number' ? params.end_at : Number(params.end_at),
                granularity: params.granularity,
                report_name: 'openapi_attempted_called_ranking',
            }, {
                iDisplayStart,
                iDisplayLength,
            });
            const data = response.aaData || [];
            this.attemptedCallsRankingOption.tableOptions.totalRows = response.iTotalRecords;
            this.attemptedCallsRankingOption.noData = data.length === 0;
            // 从对应的 Trend 中获取 Last Updated 时间
            this.attemptedCallsRankingOption.Updated = this.connectAttemptedOption.Updated;
            if (this.attemptedCallsRankingOption.noData)
                return;
            // 计算所有 API 的总调用次数（用于计算百分比）
            const totalCalls = data.reduce((sum, item) => sum + item.combo_call_count + item.call_count, 0);
            // 添加排名、计算总数和百分比
            const processedData = data.map((item, index) => {
                const itemTotal = item.combo_call_count + item.call_count;
                const percentage = totalCalls > 0 ? (itemTotal / totalCalls * 100).toFixed(2) : '0.00';
                return {
                    ...item,
                    ranking: iDisplayStart + index + 1,
                    total_call_count: itemTotal,
                    percentage: percentage,
                };
            });
            // 生成表格数据
            this.attemptedCallsRankingOption.tableOptions.rows = processedData;
            this.attemptedCallsRankingOption.tableOptions.paginationOptions.setCurrentPage = (iDisplayStart / iDisplayLength) + 1;
            // 生成图表数据 - 水平条形图
            const chartOption = {
                grid: {
                    left: '5%',
                    right: '5%',
                    top: '5%',
                    bottom: '5%',
                    containLabel: true,
                },
                xAxis: {
                    type: 'value',
                },
                yAxis: {
                    type: 'category',
                    data: processedData.map((item, index) => `${index + 1} ${this.i18n.locale === 'en' ? item.api_en_name : item.api_cn_name}`),
                    inverse: true,
                    axisLabel: {
                        interval: 0,
                        formatter: (value) => {
                            const maxLength = 20;
                            if (value.length > maxLength) {
                                return value.substring(0, maxLength) + '...';
                            }
                            return value;
                        },
                    },
                },
                series: [
                    {
                        name: this.i18n.t('backoffice.company.dataAnalysis.connect.fixed'),
                        type: 'bar',
                        stack: 'total',
                        data: processedData.map(item => item.combo_call_count),
                        itemStyle: {
                            color: '#5470C6',
                        },
                    },
                    {
                        name: this.i18n.t('backoffice.company.dataAnalysis.connect.prepaid'),
                        type: 'bar',
                        stack: 'total',
                        data: processedData.map(item => item.call_count),
                        itemStyle: {
                            color: '#91CC75',
                        },
                    }
                ],
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow',
                    },
                },
                legend: {
                    data: [
                        this.i18n.t('backoffice.company.dataAnalysis.connect.fixed'),
                        this.i18n.t('backoffice.company.dataAnalysis.connect.prepaid')
                    ],
                    bottom: 0,
                },
            };
            this.attemptedCallsRankingOption.chartOption = chartOption;
        }
        catch (error) {
            console.error('Failed to load attempted calls ranking:', error);
            this.attemptedCallsRankingOption.noData = true;
        }
        finally {
            this.attemptedCallsRankingOption.loading = false;
        }
    }
    async getUsageDetailData(params) {
        this.usageDetailOption.loading = true;
        try {
            const response = await this.companyConnectApi.getUsageDetails({
                company_id: params.company_id,
                begin_at: typeof params.begin_at === 'number' ? params.begin_at : Number(params.begin_at),
                end_at: typeof params.end_at === 'number' ? params.end_at : Number(params.end_at),
                granularity: params.granularity,
                report_name: 'openapi_successful_called',
                order_bys: params.order_bys || null,
            }, {
                iDisplayStart: params.iDisplayStart || 0,
                iDisplayLength: params.iDisplayLength || 10,
            });
            this.usageDetailOption.tableOptions.rows = response.aaData;
            this.usageDetailOption.tableOptions.totalRows = response.iTotalRecords;
            this.usageDetailOption.noData = response.aaData.length === 0;
            // 从 The Trend of Successful Calls 中获取 Last Updated 时间
            this.usageDetailOption.Updated = this.connectUsageOption.Updated;
        }
        catch (error) {
            console.error('Failed to load usage detail list:', error);
            this.usageDetailOption.tableOptions.rows = [];
            this.usageDetailOption.tableOptions.totalRows = 0;
            this.usageDetailOption.noData = true;
        }
        finally {
            this.usageDetailOption.loading = false;
        }
    }
    async downloadUsageDetail(params) {
        return this.companyConnectApi.downloadUsageDetails({
            company_id: params.company_id,
            begin_at: typeof params.begin_at === 'number' ? params.begin_at : Number(params.begin_at),
            end_at: typeof params.end_at === 'number' ? params.end_at : Number(params.end_at),
            granularity: params.granularity,
            report_name: 'openapi_successful_called',
        });
    }
    getConnectUsageData(params) {
        this.connectUsageOption.loading = true;
        const connectParams = {
            ...params,
            report_name: CompanyDataAnalysisKeyType.CONNECT_USAGE,
        };
        return this.companyDataAnalysisApi.getCompanyDataAnalysisData(connectParams).then(data => {
            this.connectUsageOption.Updated = data.data_date;
            this.connectUsageOption.noData = !data.data_detail || data.data_detail.length === 0;
            if (this.connectUsageOption.noData)
                return;
            // 生成表格数据
            this.connectUsageOption.tableOptions.rows = this.companyDataAnalysisService.getAnalysisTableRows(data.data_detail, CompanyDataAnalysisDataSet.TOTAL_COUNT);
            // 生成图表参数（使用已初始化的表格列配置）
            const chartParams = {
                data,
                dataSet: CompanyDataAnalysisDataSet.TOTAL_COUNT,
                sortList: [CompanyDataAnalysisEventKey.FIXED, CompanyDataAnalysisEventKey.PREPAID],
                colorList: ['#5470C6', '#91CC75'],
                columns: this.connectUsageOption.tableOptions.columns,
            };
            // 生成堆叠柱状图
            this.connectUsageOption.chartOption = this.companyDataAnalysisService.getStackBarCharOption(chartParams);
        }).catch(error => {
            console.error('Failed to load Connect usage data:', error);
            this.connectUsageOption.noData = true;
        }).finally(() => {
            this.connectUsageOption.loading = false;
        });
    }
    getConnectAttemptedData(params) {
        this.connectAttemptedOption.loading = true;
        const connectParams = {
            ...params,
            report_name: CompanyDataAnalysisKeyType.CONNECT_ATTEMPTED,
        };
        return this.companyDataAnalysisApi.getCompanyDataAnalysisData(connectParams).then(data => {
            this.connectAttemptedOption.Updated = data.data_date;
            this.connectAttemptedOption.noData = !data.data_detail || data.data_detail.length === 0;
            if (this.connectAttemptedOption.noData)
                return;
            // 生成表格数据
            this.connectAttemptedOption.tableOptions.rows = this.companyDataAnalysisService.getAnalysisTableRows(data.data_detail, CompanyDataAnalysisDataSet.TOTAL_COUNT);
            // 生成图表参数（使用已初始化的表格列配置）
            const chartParams = {
                data,
                dataSet: CompanyDataAnalysisDataSet.TOTAL_COUNT,
                sortList: [CompanyDataAnalysisEventKey.FIXED, CompanyDataAnalysisEventKey.PREPAID],
                colorList: ['#5470C6', '#91CC75'],
                columns: this.connectAttemptedOption.tableOptions.columns,
            };
            // 生成堆叠柱状图
            this.connectAttemptedOption.chartOption = this.companyDataAnalysisService.getStackBarCharOption(chartParams);
        }).catch(error => {
            console.error('Failed to load Connect attempted data:', error);
            this.connectAttemptedOption.noData = true;
        }).finally(() => {
            this.connectAttemptedOption.loading = false;
        });
    }
};
CompanyConnectDataAnalysisService = __decorate([
    injectable(),
    __param(0, inject(Symbols.CompanyDataAnalysisService)),
    __param(1, inject(Symbols.CompanyDataAnalysisApi)),
    __param(2, inject(Symbols.CompanyConnectApi)),
    __param(3, inject(Symbols.I18n)),
    __metadata("design:paramtypes", [Object, Object, Object, Object])
], CompanyConnectDataAnalysisService);
export { CompanyConnectDataAnalysisService };
